/**
 * RichTextView — sicheres Rendern von TipTap/HTML-Content.
 *
 * Da Bio + Listing-Description historisch Plain-Text/Markdown enthalten können
 * und ab iter112 Tiptap-HTML gespeichert wird, muss die Darstellung beides
 * verkraften. Wir erlauben eine kleine Allowlist von HTML-Tags — reicht für den
 * StarterKit-Output. Alles andere wird escaped/entfernt.
 *
 * Props:
 *  - html       Roh-HTML (String) aus TipTap oder plain text
 *  - className  optional Tailwind-Klassen (Standard: prose prose-sm)
 *  - as         Wrapper-Element (default 'div')
 *  - testId     optional
 */
import React, { useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Erlaubte Tags aus TipTap-StarterKit + Link
const ALLOWED_TAGS = new Set([
  "p", "br", "hr",
  "strong", "b", "em", "i", "s", "u",
  "h1", "h2", "h3", "h4",
  "ul", "ol", "li",
  "blockquote", "code",
  "a", "span", "div",
]);
const ALLOWED_ATTRS = {
  // iter154 · Mentions: data-mention + class für <a> erlauben
  a: new Set(["href", "target", "rel", "data-mention", "class"]),
  "*": new Set([]), // Andere Tags dürfen keine Attribute behalten
};

function sanitizeHtml(raw) {
  if (!raw) return "";
  if (typeof window === "undefined" || !window.DOMParser) {
    // SSR-Fallback: nichts rendern
    return "";
  }
  // Wenn plain text ohne HTML-Tags → nl2br
  if (!/<[a-z][\s\S]*>/i.test(raw)) {
    const escaped = raw
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
    return escaped.replace(/\n/g, "<br/>");
  }
  const doc = new window.DOMParser().parseFromString(`<div id="root">${raw}</div>`, "text/html");
  const root = doc.getElementById("root");
  if (!root) return "";

  const walk = (node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === 1) { // ELEMENT_NODE
        const tag = child.tagName.toLowerCase();
        if (!ALLOWED_TAGS.has(tag)) {
          // Ersetze das Element durch seine Kinder (aber verwerfe evtl. Attributes)
          const parent = child.parentNode;
          while (child.firstChild) parent.insertBefore(child.firstChild, child);
          parent.removeChild(child);
          continue;
        }
        // Attribute filtern
        const allowedForTag = ALLOWED_ATTRS[tag] || ALLOWED_ATTRS["*"];
        for (const attr of Array.from(child.attributes)) {
          if (!allowedForTag.has(attr.name.toLowerCase())) {
            child.removeAttribute(attr.name);
          } else if (attr.name.toLowerCase() === "href") {
            // href darf nicht mit javascript: starten
            const v = (attr.value || "").trim().toLowerCase();
            if (v.startsWith("javascript:") || v.startsWith("data:")) {
              child.removeAttribute(attr.name);
            }
          }
        }
        // <a> hardening: externe Links neu-Tab; interne (/user/, /listing/, /post/, /reel/, /studio/, /...) im gleichen Tab.
        if (tag === "a" && child.getAttribute("href")) {
          const href = (child.getAttribute("href") || "").trim();
          const isInternal =
            href.startsWith("/") ||
            child.hasAttribute("data-mention");
          if (isInternal) {
            child.removeAttribute("target");
            child.setAttribute("rel", "noopener");
          } else {
            child.setAttribute("target", "_blank");
            child.setAttribute("rel", "noopener noreferrer nofollow");
          }
        }
        walk(child);
      }
    }
  };
  walk(root);
  // iter153 · @handle-Mentions in Text-Nodes zu Links konvertieren
  linkifyMentions(root);
  return root.innerHTML;
}

// iter153 · Text-Nodes traversieren und `@handle` zu <a>-Links transformieren.
// Handle-Regex passt zu Backend (_MENTION_RE): [a-z0-9_]{3,32}, case-insensitive.
const MENTION_RE = /(^|[\s(])@([a-zA-Z0-9_]{3,32})\b/g;
function linkifyMentions(root) {
  if (typeof document === "undefined") return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const targets = [];
  let n;
  while ((n = walker.nextNode())) {
    if (!n.nodeValue || !n.nodeValue.includes("@")) continue;
    // Innerhalb <a>-Tags nicht erneut linken
    let p = n.parentNode;
    let inside = false;
    while (p && p !== root) {
      if (p.tagName === "A") { inside = true; break; }
      p = p.parentNode;
    }
    if (!inside) targets.push(n);
  }
  for (const textNode of targets) {
    const raw = textNode.nodeValue;
    if (!MENTION_RE.test(raw)) { MENTION_RE.lastIndex = 0; continue; }
    MENTION_RE.lastIndex = 0;
    const parent = textNode.parentNode;
    const frag = document.createDocumentFragment();
    let lastIdx = 0;
    let m;
    while ((m = MENTION_RE.exec(raw)) !== null) {
      const start = m.index + m[1].length; // skip leading space
      if (start > lastIdx) frag.appendChild(document.createTextNode(raw.slice(lastIdx, start)));
      const handle = m[2];
      const a = document.createElement("a");
      a.setAttribute("href", `/user/${handle}`);
      a.setAttribute("data-mention", handle);
      a.className = "mention-link";
      a.textContent = `@${handle}`;
      frag.appendChild(a);
      lastIdx = start + handle.length + 1; // +1 for '@'
    }
    if (lastIdx < raw.length) frag.appendChild(document.createTextNode(raw.slice(lastIdx)));
    parent.replaceChild(frag, textNode);
  }
}

export default function RichTextView({
  html,
  className = "prose prose-sm max-w-none",
  as: Wrapper = "div",
  testId,
}) {
  const safe = useMemo(() => sanitizeHtml(html), [html]);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  // iter155 · Interne Links (Mentions & andere /-Pfade) via React-Router — kein Full-Page-Reload
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a || !el.contains(a)) return;
      const href = a.getAttribute("href") || "";
      // Nur interne relative Pfade abfangen (nicht http(s)://, mailto:, tel:, #)
      if (!href.startsWith("/")) return;
      // Modifier-Keys respektieren (Cmd/Ctrl-Click öffnet neuen Tab)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      navigate(href);
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [safe, navigate]);

  if (!safe) return null;
  return (
    <Wrapper
      ref={wrapperRef}
      data-testid={testId}
      className={className}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}
